"use client";
import { useAppSelector } from "../../lib/redux/hooks";
import { selectResume } from "../../lib/redux/resumeSlice";
import { selectSettings } from "../../lib/redux/settingsSlice";
import type { Resume as ResumeType } from "../../lib/redux/types";
import type { ResumeSettings } from "../../lib/redux/settingsSlice";
import { ClassicBlueTemplate } from "./ClassicBlueTemplate";

interface ResumePDFProps {
  resume: ResumeType;
  settings: ResumeSettings;
  isPDF: boolean;
}

export const ResumePDF = ({ resume, settings, isPDF }: ResumePDFProps) => {
  // Use Classic Blue template if selected
  if (settings.theme.template === 'classic-blue') {
    return <ClassicBlueTemplate resume={resume} settings={settings} isPDF={isPDF} />;
  }

  // Default to Modern Gradient template
  const getDocumentSize = () => {
    switch (settings.theme.documentSize) {
      case "A4":
        return { width: "210mm", height: "297mm" };
      case "Legal":
        return { width: "216mm", height: "356mm" };
      default: // Letter
        return { width: "216mm", height: "279mm" };
    }
  };

  const documentSize = getDocumentSize();

  return (
    <div
      className={`bg-white shadow-xl mx-auto rounded-lg ${
        isPDF ? "" : "border-2 border-blue-200"
      }`}
      style={{
        width: documentSize.width,
        height: documentSize.height,
        fontFamily: settings.theme.fontFamily,
        fontSize: settings.theme.fontSize,
        padding: "20mm",
        boxSizing: "border-box",
      }}
    >
      {/* Header with colorful accent */}
      <div className="relative mb-8">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-t-lg"></div>
        <div className="text-center pt-4">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
            {resume.profile.name || "Your Name"}
          </h1>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            {resume.profile.email && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                {resume.profile.email}
              </div>
            )}
            {resume.profile.phone && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                {resume.profile.phone}
              </div>
            )}
            {resume.profile.location && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                {resume.profile.location}
              </div>
            )}
            {resume.profile.url && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                {resume.profile.url}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      {resume.profile.summary && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
            <div className="w-8 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mr-3"></div>
            Professional Summary
          </h2>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p className="text-sm text-gray-700 leading-relaxed">
              {resume.profile.summary}
            </p>
          </div>
        </div>
      )}

      {/* Work Experience */}
      {resume.workExperiences.length > 0 && resume.workExperiences[0].company && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <div className="w-8 h-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mr-3"></div>
            Work Experience
          </h2>
          <div className="space-y-6">
            {resume.workExperiences.map((experience, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-lg text-gray-900">
                    {experience.jobTitle}
                  </h3>
                  <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                    {experience.date}
                  </span>
                </div>
                <div className="text-sm text-blue-600 font-semibold mb-3">{experience.company}</div>
                {experience.descriptions.length > 0 && (
                  <ul className="text-sm text-gray-700 space-y-1">
                    {experience.descriptions.map((desc, descIdx) => (
                      <li key={descIdx} className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                        <span>{desc}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education */}
      {resume.educations.length > 0 && resume.educations[0].school && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <div className="w-8 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mr-3"></div>
            Education
          </h2>
          <div className="space-y-6">
            {resume.educations.map((education, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-lg text-gray-900">
                    {education.degree}
                  </h3>
                  <span className="text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium">
                    {education.date}
                  </span>
                </div>
                <div className="text-sm text-purple-600 font-semibold mb-2">{education.school}</div>
                {education.gpa && (
                  <div className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium mb-3 inline-block">
                    GPA: {education.gpa}
                  </div>
                )}
                {education.descriptions.length > 0 && (
                  <ul className="text-sm text-gray-700 space-y-1">
                    {education.descriptions.map((desc, descIdx) => (
                      <li key={descIdx} className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                        <span>{desc}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      {resume.projects.length > 0 && resume.projects[0].project && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <div className="w-8 h-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mr-3"></div>
            Projects
          </h2>
          <div className="space-y-6">
            {resume.projects.map((project, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-lg text-gray-900">
                    {project.project}
                  </h3>
                  <span className="text-sm bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                    {project.date}
                  </span>
                </div>
                {project.descriptions.length > 0 && (
                  <ul className="text-sm text-gray-700 space-y-1">
                    {project.descriptions.map((desc, descIdx) => (
                      <li key={descIdx} className="flex items-start">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                        <span>{desc}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills */}
      {(resume.skills.featuredSkills.some(skill => skill.skill) || resume.skills.descriptions.length > 0) && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <div className="w-8 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full mr-3"></div>
            Skills
          </h2>
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border-l-4 border-teal-500">
            {resume.skills.featuredSkills.some(skill => skill.skill) && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">Technical Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {resume.skills.featuredSkills
                    .filter(skill => skill.skill)
                    .map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm rounded-full font-medium shadow-sm"
                      >
                        {skill.skill}
                      </span>
                    ))}
                </div>
              </div>
            )}
            {resume.skills.descriptions.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Additional Skills & Certifications</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {resume.skills.descriptions.map((desc, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                      <span>{desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Section */}
      {resume.custom.descriptions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <div className="w-8 h-1 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full mr-3"></div>
            Additional Information
          </h2>
          <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border-l-4 border-pink-500">
            <ul className="text-sm text-gray-700 space-y-2">
              {resume.custom.descriptions.map((desc, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="w-2 h-2 bg-pink-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                  <span>{desc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
